import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getInicioStore } from "../../services/public/tiendaInicio";
import { fileURL } from "../../utils/fileURL";
import StoreInicioCarousel from "./StoreInicioCarousel";
import ProductCarousel from "../../components/store/ProductCarousel";
import { useConfiguracionTiendaPublic } from "./hooks/useConfiguracionTiendaPublic";
import "./StoreInicio.css";

const ACCESS_ROUTE_MAP = {
  CATEGORIAS: "/store/categorias",
  MARCAS: "/store/marcas",
  NOVEDADES: "/store/productos?novedades=1",
  OFERTAS: "/store/productos?ofertas=1",
};

const DEFAULT_CTA_PATH = "/store/productos";

function storeCtaDestino(rawUrl) {
  const s = String(rawUrl ?? "").trim();
  if (!s) return { kind: "app", to: DEFAULT_CTA_PATH };
  const lower = s.toLowerCase();
  if (
    lower.startsWith("http://") ||
    lower.startsWith("https://") ||
    lower.startsWith("//") ||
    lower.startsWith("mailto:") ||
    lower.startsWith("tel:")
  ) {
    return { kind: "external", href: s };
  }
  const path = s.startsWith("/") ? s : `/${s}`;
  return { kind: "app", to: path };
}

function StoreCtaButton({ inicio }) {
  const dest = storeCtaDestino(inicio?.cta_boton_url);
  const label = inicio?.cta_boton_texto || "Ir al catálogo";
  if (dest.kind === "external") {
    return (
      <a
        href={dest.href}
        className="btn btn--primary"
        target="_blank"
        rel="noopener noreferrer"
      >
        {label}
      </a>
    );
  }
  return (
    <Link to={dest.to} className="btn btn--primary">
      {label}
    </Link>
  );
}

function StoreInicio() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { configuracionTienda } = useConfiguracionTiendaPublic();
  const rootRef = useRef(null);

  useEffect(() => {
    const cargar = async () => {
      try {
        const response = await getInicioStore();
        setData(response?.data || null);
      } catch (error) {
        console.error("Error cargando inicio:", error);
      } finally {
        setLoading(false);
      }
    };

    cargar();
  }, []);

  // Aparición suave al hacer scroll (respeta prefers-reduced-motion).
  useEffect(() => {
    if (loading) return undefined;
    const root = rootRef.current;
    if (!root) return undefined;

    const els = root.querySelectorAll(".reveal");
    const reduce =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduce || !("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("is-visible"));
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );

    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [loading, data]);

  if (loading) {
    return (
      <div className="store-home" aria-busy="true" aria-label="Cargando inicio">
        <div className="sk sk-carousel" />
        <div className="sk-hero">
          <div className="sk-hero__copy">
            <div className="sk sk-line sk-line--tag" />
            <div className="sk sk-line sk-line--title" />
            <div className="sk sk-line sk-line--title sk-line--short" />
            <div className="sk sk-line" />
            <div className="sk sk-line sk-line--short" />
            <div className="sk sk-line sk-btn" />
          </div>
          <div className="sk sk-hero__media" />
        </div>
        <div className="sk-links">
          {Array.from({ length: 4 }).map((_, i) => (
            <div className="sk sk-link" key={i} />
          ))}
        </div>
        <div className="sk-cards">
          {Array.from({ length: 5 }).map((_, i) => (
            <div className="sk-card" key={i}>
              <div className="sk sk-card__img" />
              <div className="sk sk-line sk-card__t" />
              <div className="sk sk-line sk-card__p" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const store = data?.store || {};
  const inicio = data?.inicio || {};
  const accesos = Array.isArray(data?.accesos) ? data.accesos : [];
  const destacadosAll = Array.isArray(data?.destacados) ? data.destacados : [];
  // Mostrar como máximo 8 en el inicio (los cards son grandes).
  const destacados = destacadosAll.slice(0, 8);
  // Mostrar el botón "Ver todos" siempre que haya al menos un producto
  // destacado (así el cliente puede abrir la lista completa desde el inicio).
  const mostrarVerTodos = destacados.length > 0;
  const carruselApi = data?.carrusel || {};
  const carruselSlides =
    carruselApi?.activo && Array.isArray(carruselApi.items)
      ? carruselApi.items
          .map((it) => ({
            id: String(it.tienda_img_carrusel_id),
            src: (fileURL(it.url_img) || it.url_img || "").trim(),
            href: it.enlace_href || null,
            externo: !!it.enlace_externo,
          }))
          .filter((s) => s.src)
      : [];

  return (
    <div className="store-home" ref={rootRef}>
      {carruselSlides.length > 0 && <StoreInicioCarousel items={carruselSlides} />}

      <section className="hero reveal">
        <div>
          {inicio?.hero_tag && <span className="hero__tag">{inicio.hero_tag}</span>}

          <h1 className="hero__title">
            {inicio?.hero_titulo || store?.nombre_comercial || store?.nombre || "Bienvenido"}
          </h1>

          {inicio?.hero_descripcion && (
            <p className="hero__text">{inicio.hero_descripcion}</p>
          )}

          <div className="hero__actions">
            {Number(inicio?.hero_boton_primario_mostrar) === 1 && (
              <Link to="/store/productos" className="btn btn--primary">
                {inicio?.hero_boton_primario_texto || "Ver productos"}
              </Link>
            )}
          </div>
        </div>

        <div className="hero__image-wrap">
          <img
            className="hero__image"
            src={fileURL(inicio?.hero_imagen_url) || ""}
            alt={inicio?.hero_imagen_alt || "Presentación de tienda"}
            loading="lazy"
            decoding="async"
          />
        </div>
      </section>

      {accesos.length > 0 && (
        <section className="quick-links reveal">
          {accesos.map((item) => {
            const route = ACCESS_ROUTE_MAP[item.clave] || "/store/productos";

            return (
              <Link
                key={item.tienda_inicio_acceso_id}
                to={route}
                className="quick-links__item"
              >
                {item.titulo}
              </Link>
            );
          })}
        </section>
      )}

      <section className="section reveal">
        <div className="section__head">
          <div>
            <h2 className="section__title">
              {inicio?.destacados_titulo || "Productos destacados"}
            </h2>
            {inicio?.destacados_descripcion && (
              <p className="section__text">{inicio.destacados_descripcion}</p>
            )}
          </div>

          {mostrarVerTodos && (
            <Link to="/store/productos?destacados=1" className="btn btn--secondary">
              {inicio?.destacados_boton_texto || "Ver todos"}
            </Link>
          )}
        </div>

        {destacados.length > 0 ? (
          <ProductCarousel
            products={destacados}
            configuracionTienda={configuracionTienda}
            onSelect={(p) => navigate(`/store/product/${p.producto_id}`)}
          />
        ) : (
          <p className="section__empty">
            Aún no hay productos destacados para mostrar.
          </p>
        )}
      </section>

      {(inicio?.banner_titulo || inicio?.banner_descripcion || inicio?.banner_texto_lateral) && (
        <section className="banner reveal">
          <div>
            <h2 className="banner__title">{inicio?.banner_titulo}</h2>
            <p className="banner__text">{inicio?.banner_descripcion}</p>
          </div>
          <div className="banner__box">{inicio?.banner_texto_lateral}</div>
        </section>
      )}

      {(inicio?.cta_titulo || inicio?.cta_descripcion || Number(inicio?.cta_boton_mostrar) === 1) && (
        <section className="cta reveal">
          <h2>{inicio?.cta_titulo || "Explora la tienda"}</h2>
          <p>{inicio?.cta_descripcion || "Encuentra lo que buscas en nuestro catálogo."}</p>

          {Number(inicio?.cta_boton_mostrar) === 1 && <StoreCtaButton inicio={inicio} />}
        </section>
      )}
    </div>
  );
}

export default StoreInicio;